import { offeredSizes } from "./artwork-sizing";
import type { CanvasVariant } from "../canvas/variant-resolver";

interface Printable {
  widthPx: number;
  heightPx: number;
}

/**
 * The artworks that can actually be bought right now.
 *
 * A work is offered in a size only if its shape matches a stocked format to
 * within the aspect tolerance AND its master carries that size at print
 * resolution. A work that passes neither has nothing to sell, and used to sit
 * in the gallery anyway — clicking it reached a page whose only content was
 * "no size available for this work". With the five-format catalogue that was
 * 12 of 159: Klimt's Kiss, Monet's water lilies, Renoir, Turner, Friedrich and
 * the rest, all near-square works with no square format stocked.
 *
 * Computed rather than stored deliberately. The size list is expected to grow,
 * and a flag on the artwork would have to be remembered and switched back —
 * this way a work reappears the moment a format it fits is added, and
 * disappears again if that format is retired.
 *
 * While the variants are still loading, everything is shown: an empty list
 * means "not known yet", and emptying the gallery on a slow request would look
 * like a broken shop.
 */
export function printableArtworks<T extends Printable>(
  artworks: T[],
  variants: CanvasVariant[] | null | undefined,
): T[] {
  if (!variants || variants.length === 0) return artworks;

  return artworks.filter(
    (artwork) =>
      offeredSizes(
        { widthPx: artwork.widthPx, heightPx: artwork.heightPx },
        variants,
      ).length > 0,
  );
}
